import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { JsonForms } from "@jsonforms/react";
import {
    materialRenderers,
    materialCells,
} from "@jsonforms/material-renderers";
import {
    ErrorTranslator,
    JsonFormsRendererRegistryEntry,
    createAjv,
} from "@jsonforms/core";
import { LoadingButton } from "../framework/loadingButton";
import { FormTemplateSchema } from "../../models/form/formTemplate";
import { ValidationMode } from "@jsonforms/core";
import {
    AttachmentRenderer,
    attachmentRendererTester,
} from "./attachmentRenderer";

const allRenderers: JsonFormsRendererRegistryEntry[] = [
    ...materialRenderers,
    { tester: attachmentRendererTester, renderer: AttachmentRenderer },
];

const ajv = createAjv();
ajv.addFormat("attachment", true);

interface FormFillViewProps {
    formData: unknown;
    jsonSchema: FormTemplateSchema;
    saving: boolean;
    handleSave: (formData: unknown) => Promise<void>;
    onDirtyChange?: (dirty: boolean) => void;
    attachmentSessionId?: string;
}

export const FormFillView = ({
    formData: propFormData,
    jsonSchema,
    saving,
    handleSave,
    onDirtyChange,
    attachmentSessionId,
}: FormFillViewProps) => {
    const { t } = useTranslation();
    const formRef = useRef<HTMLDivElement>(null);

    const [formData, setFormData] = useState<unknown>(propFormData);
    const [errors, setErrors] = useState<unknown[]>([]);
    const [dirty, setDirty] = useState(false);
    const initializedRef = useRef(false);
    const [validationMode, setValidationMode] =
        useState<ValidationMode>("ValidateAndHide");

    const translateError: ErrorTranslator = useCallback(
        (error) => {
            const key = `forms.validation.${error.keyword}`;
            const params = error.params as Record<string, unknown>;
            const translated = t(key, params as never);
            return translated !== key
                ? translated
                : (error.message ?? t("forms.validation.type"));
        },
        [t]
    );

    useEffect(() => {
        setFormData(propFormData);
    }, [propFormData]);

    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    const scrollToFirstError = () => {
        if (!formRef.current) return;
        const errorElement = formRef.current.querySelector(
            ".Mui-error, [class*='ValidationIcon']"
        );
        errorElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const onSave = async () => {
        if (errors.length > 0) {
            setValidationMode("ValidateAndShow");
            setTimeout(scrollToFirstError, 100);
            return;
        }

        await handleSave(formData);
    };

    return (
        <>
            <Box
                ref={formRef}
                data-testid="form-fill-container"
                sx={{ "& .MuiRadioGroup-root": { order: 1 } }}
            >
                <JsonForms
                    schema={jsonSchema.schema}
                    uischema={jsonSchema.uiSchema}
                    data={formData}
                    renderers={allRenderers}
                    cells={materialCells}
                    config={{
                        showUnfocusedDescription: true,
                        attachmentSessionId,
                    }}
                    ajv={ajv}
                    i18n={{ translateError }}
                    onChange={({ data, errors: errs }) => {
                        setFormData(data);
                        setErrors(errs ?? []);
                        if (!initializedRef.current) {
                            initializedRef.current = true;
                            return;
                        }
                        if (!dirty) {
                            setDirty(true);
                            onDirtyChange?.(true);
                        }
                    }}
                    validationMode={validationMode}
                />
            </Box>
            <Box mt={2} mb={2}>
                <LoadingButton
                    onClick={() => void onSave()}
                    variant="contained"
                    color="success"
                    disabled={saving || !dirty}
                    loading={saving}
                    fullWidth
                    data-testid="form-fill-save"
                >
                    {t("common.save")}
                </LoadingButton>
            </Box>
        </>
    );
};
